import React, { useState, useEffect } from 'react';
import { 
  Mic, Phone, Users, HardDrive, Clock, Zap, TrendingUp, 
  AlertTriangle, CheckCircle, BarChart3, Bell, X, Mail
} from 'lucide-react';
import { useClientSettings } from '../../contexts/ClientSettingsContext';
import { usageAlertsAPI } from '../../services/api';

const UsageCard = ({ icon: Icon, title, used, limit, unit, percent, color = 'teal' }) => {
  const getStatusColor = (pct) => {
    if (pct >= 90) return 'red';
    if (pct >= 75) return 'yellow';
    return 'teal';
  };
  
  const statusColor = getStatusColor(percent);
  
  return (
    <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 text-${color}-400`} />
          <span className="text-gray-300 font-medium">{title}</span>
        </div>
        {percent >= 90 && (
          <AlertTriangle className="w-4 h-4 text-red-400" />
        )}
      </div>
      
      <div className="flex items-end gap-2 mb-3">
        <span className="text-3xl font-bold text-white">{used}</span>
        <span className="text-gray-500 text-lg">/ {limit} {unit}</span>
      </div>
      
      {/* Progress bar */}
      <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
        <div 
          className={`h-full rounded-full transition-all duration-500 ${
            statusColor === 'red' ? 'bg-red-500' :
            statusColor === 'yellow' ? 'bg-yellow-500' :
            'bg-teal-400'
          }`}
          style={{ width: `${Math.min(100, percent)}%` }}
        />
      </div>
      
      <div className="flex justify-between mt-2 text-sm">
        <span className={`${
          statusColor === 'red' ? 'text-red-400' :
          statusColor === 'yellow' ? 'text-yellow-400' :
          'text-gray-500'
        }`}>
          {percent}% used
        </span>
        <span className="text-gray-500">
          {limit - used} remaining
        </span>
      </div>
    </div>
  );
};

const StatCard = ({ icon: Icon, title, value, subtitle, color = 'blue' }) => (
  <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5">
    <div className="flex items-center gap-3 mb-2">
      <div className={`p-2 rounded-lg bg-${color}-400/10`}>
        <Icon className={`w-5 h-5 text-${color}-400`} />
      </div>
      <span className="text-gray-400">{title}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {subtitle && <div className="text-gray-500 text-sm mt-1">{subtitle}</div>}
  </div>
);

const ClientUsageDashboard = () => {
  const { usage, loading, planName, hasFeature, clientId } = useClientSettings();
  const [alerts, setAlerts] = useState([]);
  const [alertsDismissed, setAlertsDismissed] = useState(false);
  const [testingAlert, setTestingAlert] = useState(false);
  const [alertHistory, setAlertHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Check for usage alerts
  useEffect(() => {
    const checkAlerts = async () => {
      if (!clientId) return;
      try {
        const response = await usageAlertsAPI.check(clientId);
        if (response.data.success && response.data.alerts.length > 0) {
          setAlerts(response.data.alerts);
        }
      } catch (error) {
        console.error('Error checking alerts:', error);
      }
    };
    checkAlerts();
  }, [clientId]);

  // Fetch alert history
  useEffect(() => {
    const fetchHistory = async () => {
      if (!clientId || !showHistory) return;
      try {
        const response = await usageAlertsAPI.getHistory(clientId);
        if (response.data.success) {
          setAlertHistory(response.data.alerts);
        }
      } catch (error) {
        console.error('Error fetching alert history:', error);
      }
    };
    fetchHistory();
  }, [clientId, showHistory]);

  const handleTestAlert = async () => {
    if (!clientId) return;
    setTestingAlert(true);
    try {
      const response = await usageAlertsAPI.sendTest(clientId);
      if (response.data.success) {
        alert(response.data.message);
      }
    } catch (error) {
      alert('Failed to send test alert');
    } finally {
      setTestingAlert(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-800 rounded w-48"></div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-40 bg-gray-800 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 text-center">
        <AlertTriangle className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
        <p className="text-yellow-400">Unable to load usage data</p>
      </div>
    );
  }

  const { usage: usageData, call_stats: callStats, billing_period: billingPeriod } = usage;

  return (
    <div className="space-y-6" data-testid="client-usage-dashboard">
      {/* Usage Alerts Banner */}
      {alerts.length > 0 && !alertsDismissed && (
        <div 
          className={`rounded-xl p-4 border ${
            alerts.some(a => a.level === 'critical') 
              ? 'bg-red-500/10 border-red-500/30' 
              : 'bg-yellow-500/10 border-yellow-500/30'
          }`}
          data-testid="usage-alerts-banner"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                alerts.some(a => a.level === 'critical') ? 'text-red-400' : 'text-yellow-400'
              }`} />
              <div>
                <h3 className={`font-semibold ${
                  alerts.some(a => a.level === 'critical') ? 'text-red-400' : 'text-yellow-400'
                }`}>
                  {alerts.some(a => a.level === 'critical') 
                    ? '⚠️ Critical Usage Alert' 
                    : '⚠️ Usage Warning'}
                </h3>
                <p className="text-gray-400 text-sm mt-1">
                  {alerts.length === 1 
                    ? `${alerts[0].metric.replace(/_/g, ' ')} is at ${alerts[0].percent}% capacity`
                    : `${alerts.length} resources are approaching their limits`
                  }
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {alerts.map((alert) => (
                    <span 
                      key={`${alert.metric}-${alert.level}`}
                      className={`px-2 py-1 rounded text-xs ${
                        alert.level === 'critical' 
                          ? 'bg-red-500/20 text-red-400' 
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}
                    >
                      {alert.metric.replace(/_/g, ' ')}: {alert.percent}%
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <button 
              onClick={() => setAlertsDismissed(true)}
              className="text-gray-500 hover:text-gray-300 transition"
              data-testid="dismiss-alerts-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Usage & Quotas</h2>
          <p className="text-gray-400 text-sm">
            Plan: <span className="text-teal-400 font-medium">{planName}</span>
            {billingPeriod && (
              <> • Billing period: {new Date(billingPeriod.start).toLocaleDateString()} - {new Date(billingPeriod.end).toLocaleDateString()}</>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 px-3 py-1.5 bg-gray-800 text-gray-300 rounded-lg text-sm hover:bg-gray-700 transition"
            data-testid="view-alert-history-btn"
          >
            <Bell className="w-4 h-4" />
            Alert History
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-400/10 text-teal-400 rounded-full text-sm">
            <CheckCircle className="w-4 h-4" />
            Active
          </div>
        </div>
      </div>

      {/* Alert History Panel */}
      {showHistory && (
        <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5" data-testid="alert-history-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-400" />
              Alert Email History
            </h3>
            <button
              onClick={handleTestAlert}
              disabled={testingAlert}
              className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/20 text-purple-400 rounded-lg text-sm hover:bg-purple-500/30 transition disabled:opacity-50"
              data-testid="test-alert-btn"
            >
              {testingAlert ? 'Sending...' : 'Send Test Alert'}
            </button>
          </div>
          {alertHistory.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No alert emails have been sent yet</p>
          ) : (
            <div className="space-y-3 max-h-60 overflow-y-auto">
              {alertHistory.map((history) => (
                <div key={history.sent_at || history.id} className="bg-gray-800/50 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400 text-sm">
                      {new Date(history.sent_at).toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500">
                      {history.alerts.length} alert(s)
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {history.alerts.map((alert) => (
                      <span 
                        key={`${history.sent_at || 'h'}-${alert.metric}`}
                        className={`px-2 py-0.5 rounded text-xs ${
                          alert.level === 'critical' 
                            ? 'bg-red-500/20 text-red-400' 
                            : 'bg-yellow-500/20 text-yellow-400'
                        }`}
                      >
                        {alert.metric.replace(/_/g, ' ')}: {alert.percent}%
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Usage Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <UsageCard
          icon={Mic}
          title="Voice Agents"
          used={usageData.voice_agents?.used || 0}
          limit={usageData.voice_agents?.limit || 5}
          unit="agents"
          percent={usageData.voice_agents?.percent || 0}
        />
        
        <UsageCard
          icon={Phone}
          title="Phone Numbers"
          used={usageData.phone_numbers?.used || 0}
          limit={usageData.phone_numbers?.limit || 2}
          unit="numbers"
          percent={usageData.phone_numbers?.percent || 0}
        />
        
        <UsageCard
          icon={Users}
          title="End Users"
          used={usageData.end_users?.used || 0}
          limit={usageData.end_users?.limit || 10}
          unit="users"
          percent={usageData.end_users?.percent || 0}
        />
        
        <UsageCard
          icon={Clock}
          title="Monthly Minutes"
          used={Math.round(usageData.monthly_minutes?.used || 0)}
          limit={usageData.monthly_minutes?.limit || 500}
          unit="min"
          percent={usageData.monthly_minutes?.percent || 0}
        />
        
        <UsageCard
          icon={HardDrive}
          title="Knowledge Base"
          used={usageData.knowledge_base?.used_mb || 0}
          limit={usageData.knowledge_base?.limit_mb || 50}
          unit="MB"
          percent={usageData.knowledge_base?.percent || 0}
        />
        
        <UsageCard
          icon={Zap}
          title="Concurrent Calls"
          used={usageData.concurrent_calls?.active || 0}
          limit={usageData.concurrent_calls?.limit || 3}
          unit="calls"
          percent={usageData.concurrent_calls?.percent || 0}
        />
      </div>

      {/* Call Statistics */}
      {hasFeature('analytics_dashboard') && callStats && (
        <>
          <h3 className="text-lg font-semibold text-white mt-8 mb-4">This Month&apos;s Call Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={Phone}
              title="Total Calls"
              value={callStats.total_calls || 0}
              subtitle="This billing period"
              color="blue"
            />
            <StatCard
              icon={CheckCircle}
              title="Completed"
              value={callStats.completed_calls || 0}
              subtitle={`${callStats.total_calls ? Math.round((callStats.completed_calls / callStats.total_calls) * 100) : 0}% success rate`}
              color="green"
            />
            <StatCard
              icon={Clock}
              title="Total Duration"
              value={`${Math.round(callStats.total_duration_minutes || 0)} min`}
              subtitle="Talk time"
              color="purple"
            />
            <StatCard
              icon={TrendingUp}
              title="Avg Duration"
              value={`${Math.round(callStats.avg_duration_seconds || 0)}s`}
              subtitle="Per call"
              color="orange"
            />
          </div>
        </>
      )}

      {/* Limits Info */}
      <div className="bg-[#0d1117] border border-gray-800 rounded-xl p-5 mt-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-teal-400" />
          Plan Limits
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Max Call Duration</span>
            <p className="text-white font-medium">{usageData.max_call_duration?.limit_minutes || 30} minutes</p>
          </div>
          <div>
            <span className="text-gray-500">Concurrent Calls</span>
            <p className="text-white font-medium">{usageData.concurrent_calls?.limit || 3} max</p>
          </div>
          <div>
            <span className="text-gray-500">Monthly Minutes</span>
            <p className="text-white font-medium">{usageData.monthly_minutes?.limit || 500} min/month</p>
          </div>
          <div>
            <span className="text-gray-500">Knowledge Base</span>
            <p className="text-white font-medium">{usageData.knowledge_base?.limit_mb || 50} MB</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientUsageDashboard;
